package com.trevorwiebe.timesheet.admin.reporting.domain

import com.trevorwiebe.timesheet.mileage.domain.model.unitNumberOrder
import com.trevorwiebe.timesheet.core.domain.model.Customer
import com.trevorwiebe.timesheet.core.domain.model.DatabaseUserModel
import com.trevorwiebe.timesheet.loads.domain.TruckMatch
import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.loads.domain.model.drops
import com.trevorwiebe.timesheet.mileage.domain.model.Equipment
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import kotlinx.datetime.LocalDate

data class LoadWithoutTruck(
    val load: Load,
    val date: LocalDate,
    val loadNumber: String,
    val customerName: String,
    val driverName: String,
    val dropNames: String,
    // Unit numbers of the trucks that matched, when more than one did.
    val candidateUnits: List<String>,
) {
    val statusText: String
        get() = if (candidateUnits.isEmpty()) {
            "No matching mileage entry"
        } else {
            "Multiple trucks matched: ${candidateUnits.joinToString(", ")}"
        }
}

/** Loads from [loads] whose truck could not be resolved to exactly one unit, oldest first. */
fun findLoadsWithoutTruck(
    loads: List<Load>,
    matches: Map<String, TruckMatch>,
    customers: List<Customer>,
    users: List<DatabaseUserModel>,
    locations: List<Location>,
    equipment: List<Equipment>,
): List<LoadWithoutTruck> = loads.mapNotNull { load ->
    val candidates = when (val match = matches[load.id]) {
        null, TruckMatch.None -> emptyList()
        is TruckMatch.Ambiguous -> match.equipmentIds.map { id ->
            equipment.firstOrNull { it.id == id }?.unitNumber ?: id
        }.sortedWith(unitNumberOrder)
        is TruckMatch.Manual, is TruckMatch.Matched -> return@mapNotNull null
    }
    LoadWithoutTruck(
        load = load,
        date = load.scheduledDate ?: load.originalDate,
        loadNumber = load.loadNumber,
        customerName = customers.firstOrNull { it.id == load.customerId }
            ?.let { it.contactName.ifBlank { it.companyName } } ?: "Unknown customer",
        driverName = users.firstOrNull { it.id == load.employeeId }?.displayName ?: "Unknown driver",
        dropNames = load.drops.joinToString(", ") { stop ->
            locations.firstOrNull { it.id == stop.locationId }?.name ?: "—"
        },
        candidateUnits = candidates,
    )
}.sortedBy { it.date }
